import { IStore } from "@/redux/defaultStore";
import getConfig from "@/utils/getConfig";
import { Token } from "@devour/client";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";

export interface QuestProgress {
    questId: string;
    questName: string;
    progressMade: number;
    totalRequired: number;
}

export interface Milestone {
    type: string;
    description: string;
    xpAwarded?: number;
}

export interface SessionInfo {
    sessionId?: string;
    gameId: string;
    gameTitle: string;
    gameThumbnail?: string;
    lastPlayed: string;
    sessionDuration: number;
    xpEarned: number;
    fuelEarned: number;
    questsCompleted: number;
    questProgress: QuestProgress[];
    milestonesReached: Milestone[];
    averageComparison?: {
        duration: "above" | "below" | "average";
        xpEarned: "above" | "below" | "average";
        percentDiff: number;
    };
}

export interface RecentSession {
    sessionId: string;
    gameId: string;
    gameTitle: string;
    gameThumbnail?: string;
    duration: number;
    xpEarned: number;
    questsCompleted: number;
    timestamp: string;
}

export interface LastSessionResponse {
    lastSession: SessionInfo | null;
    recentSessions: RecentSession[];
    todaySummary: {
        totalXP: number;
        totalTime: number;
        uniqueGames: number;
    };
    userAverages: {
        sessionDuration: number;
        xpPerSession: number;
        questsPerSession: number;
    };
}

export const LAST_SESSION_QUERY_KEY = "devourplay-last-session";

async function fetchLastSession(fullToken: Token, sessionId?: string): Promise<LastSessionResponse> {
    let url = `${getConfig(fullToken).basePath}/devourplay/last-session`;
    if (sessionId) {
        url += `?sessionId=${sessionId}`;
    }

    const response = await fetch(url, {
        headers: {
            Authorization: `Bearer ${fullToken.token}`,
            "Content-Type": "application/json",
        },
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch last session: ${response.status} ${response.statusText}`);
    }

    return response.json();
}

export const useGetLastSession = (sessionId?: string) => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    const { data, isLoading, refetch, error } = useQuery<LastSessionResponse>({
        queryKey: [LAST_SESSION_QUERY_KEY, fullToken?.id, sessionId],
        queryFn: () => fetchLastSession(fullToken!, sessionId),
        staleTime: 60000, // Cache for 1 minute
        enabled: !!fullToken?.token,
        refetchOnWindowFocus: false,
    });

    return {
        data: data || {
            lastSession: null,
            recentSessions: [],
            todaySummary: {
                totalXP: 0,
                totalTime: 0,
                uniqueGames: 0,
            },
            userAverages: {
                sessionDuration: 60,
                xpPerSession: 100,
                questsPerSession: 1,
            },
        },
        isLoading,
        error,
        refetch,
    };
};